"use client";

import React from "react";
import { MessageSquare, Shield, Users } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
const slides = [{
  id: 1,
  title: "Command-Native Workflows",
  description: "Execute complex operations directly through iMessage. Use standard syntax like '/deploy-agent-alpha' to trigger local-first macOS agents with near-zero latency.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605362/img/synk_lounge_chat_interface_dark.png",
  alt: "iMessage interface showing a user sending the deploy command to qOS",
  icon: MessageSquare
}, {
  id: 2,
  title: "Structured Data Tables",
  description: "Receive high-fidelity system responses. qOS renders structured data tables for 'System Resources' and 'Memory' directly within your conversation thread.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605362/img/synk_staff_sidebar_hierarchy_dark.png",
  alt: "qOS response showing a technical table of system resources within iMessage",
  icon: Users
}, {
  id: 3,
  title: "Cryptographic Approvals",
  description: "Maintain absolute sovereignty. Every sensitive tool execution requires an interactive approval workflow, ensuring your private agent never acts without consent.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605362/img/synk_encrypted_vault_settings_dark.png",
  alt: "Security approval prompt in iMessage for a sensitive qOS tool execution",
  icon: Shield
}];
export default function TheLoungeInterface() {
  return <section data-section-id="155786" id="the-imessage-interface" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: `
            linear-gradient(to right, hsl(240 5% 15%) 1px, transparent 1px),
            linear-gradient(to bottom, hsl(240 5% 15%) 1px, transparent 1px)
          `,
      backgroundSize: "4rem 4rem"
    }} />
      
      {}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_20%,var(--background)_100%)]" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-2xl">
          <h2 className="font-sans text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-foreground">
            The iMessage Interface
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-sans">
            Control your infrastructure through the interface you already use. qOS turns iMessage into a powerful command-line environment with native macOS security and glass-morphic UI accents.
          </p>
        </div>

        <div className="relative mx-auto max-w-6xl">
          <Carousel opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8 py-12">
              {slides.map((slide, i) => <CarouselItem key={slide.id} data-index={i} className="pl-4 md:pl-8 md:basis-4/5 lg:basis-3/4">
                  <div className="flex flex-col gap-8">
                    {}
                    <div className="group relative mx-auto w-full" style={{
                  perspective: "2000px"
                }}>
                      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-white/10 bg-card/40 backdrop-blur-2xl shadow-2xl transition-all duration-700 ease-out [transform:rotateY(-12deg)_rotateX(4deg)] group-hover:scale-[1.02] group-hover:[transform:rotateY(0deg)_rotateX(0deg)]">
                        {}
                        <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-tr from-transparent via-primary/5 to-primary/10 mix-blend-overlay" />
                        
                        <img src={slide.image} alt={slide.alt} className="h-full w-full object-cover opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
                        
                        {}
                        <div className="absolute inset-0 z-20 pointer-events-none shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] rounded-2xl" />
                      </div>
                    </div>

                    {}
                    <div className="mx-auto max-w-2xl text-center transition-opacity duration-500">
                      <div className="mb-4 flex justify-center">
                        <div className="flex size-12 items-center justify-center rounded-full bg-secondary/50 border border-border">
                          <slide.icon className="size-6 text-primary" />
                        </div>
                      </div>
                      <h3 className="font-sans text-2xl font-semibold tracking-tight text-foreground">
                        {slide.title}
                      </h3>
                      <p className="mt-2 text-muted-foreground font-sans">
                        {slide.description}
                      </p>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="hidden md:block">
              <CarouselPrevious className="left-0 xl:-left-12 bg-background/80 backdrop-blur-md border-border text-foreground hover:bg-secondary hover:text-secondary-foreground" />
              <CarouselNext className="right-0 xl:-right-12 bg-background/80 backdrop-blur-md border-border text-foreground hover:bg-secondary hover:text-secondary-foreground" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}